(() => {
  if (customElements.get('social-content')) return;

  class SocialContent extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.trackObserver?.disconnect();
      this.trackObserver = new MutationObserver(this.handleTrackChange);
      const track = this.querySelector('[data-social-content-track]');
      if (track) this.trackObserver.observe(track, { childList: true });
      this.itemSignature = this.getItemSignature();

      window.addEventListener('dieux:swiper:ready', this.mountCarousel, { signal });
      this.mountCarousel();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.trackObserver?.disconnect();
      cancelAnimationFrame(this.reinitializeFrame || 0);
      this.reinitializeFrame = 0;
      this.carouselController?.destroy();
      this.carouselController = null;
    }

    /**
     * The rail's own items, in the order the section rendered them.
     *
     * @returns {HTMLElement[]} Rendered social items, without loop fill clones.
     */
    getSourceItems() {
      const track = this.querySelector('[data-social-content-track]');
      if (!(track instanceof HTMLElement)) return [];

      const items = Array.from(track.children).filter(
        (element) =>
          element instanceof HTMLElement
          && element.matches('[data-social-content-item-id]')
          && !element.hasAttribute('data-swiper-loop-clone')
      );

      return items.sort((first, second) => {
        const firstIndex = Number(first.dataset.swiperSlideIndex ?? Number.NaN);
        const secondIndex = Number(second.dataset.swiperSlideIndex ?? Number.NaN);
        if (!Number.isFinite(firstIndex) || !Number.isFinite(secondIndex)) return 0;
        return firstIndex - secondIndex;
      });
    }

    /**
     * Identifies the current item set, independent of the order it sits in.
     *
     * @returns {string} Signature of the rendered item set.
     */
    getItemSignature() {
      return this.getSourceItems()
        .map((item) => item.dataset.socialContentItemId || '')
        .sort()
        .join('|');
    }

    mountCarousel = () => {
      if (this.carouselController || !window.DieuxSwiper || !this.isConnected) return;

      const viewport = this.querySelector('[data-social-content-viewport]');
      const track = this.querySelector('[data-social-content-track]');
      if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement)) return;

      this.carouselController = window.DieuxSwiper.mount({
        owner: this,
        viewport,
        track,
        stateTarget: this,
        previousButton: this.querySelector('[data-swiper-button="previous"]'),
        nextButton: this.querySelector('[data-swiper-button="next"]'),
        unavailableControls: 'disabled',
        mousewheel: true,
        gapProperty: '--social-content-gap',
        gapSource: this,
        loopFill: true,
        options: () => ({
          slidesPerView: 'auto',
          slidesPerGroup: 1,
          slidesPerGroupAuto: false,
          loop: this.getSourceItems().length > 1,
          a11y: {
            enabled: false,
          },
        }),
        onStateChange: ({ state, controller }) => {
          if (state === 'enhanced') requestAnimationFrame(() => controller.refresh());
        },
      });
    };

    handleTrackChange = () => {
      const signature = this.getItemSignature();
      if (signature === this.itemSignature) return;

      this.itemSignature = signature;
      this.reinitialize();
    };

    reinitialize() {
      this.carouselController?.destroy();
      this.carouselController = null;
      if (this.reinitializeFrame) return;

      this.reinitializeFrame = requestAnimationFrame(() => {
        this.reinitializeFrame = 0;
        this.mountCarousel();
      });
    }

    revealBlock(blockId, sourceElement) {
      if (!blockId) return;

      const items = this.getSourceItems();
      const item =
        sourceElement instanceof Element
          ? sourceElement.closest('[data-social-content-item-id]')
          : items.find((candidate) => candidate.dataset.socialContentItemId === blockId);
      if (!(item instanceof HTMLElement) || !this.contains(item)) return;

      this.mountCarousel();
      const itemIndex = items.indexOf(item);
      Promise.resolve(this.carouselController?.promote()).then(() => {
        const instance = this.carouselController?.instance;
        if (instance && !instance.destroyed && itemIndex >= 0) {
          if (instance.params.loop) instance.slideToLoop(itemIndex, 0);
          else instance.slideTo(itemIndex, 0);
          return;
        }

        const viewport = this.querySelector('[data-social-content-viewport]');
        if (viewport instanceof HTMLElement) viewport.scrollLeft = item.offsetLeft;
      });
    }
  }

  customElements.define('social-content', SocialContent);
})();
